// ooc-rest-truth.ts — R-124 (Janus lane N1, 2026-10-01): the OUT-OF-COMBAT TURN ECONOMY decided by the encounter page.
//
// Replaces sync_out_of_combat_encounter_creature_turn_economy(), which PHP ran on every hydrate / table render / form request and on
// ~60 other paths (a page render that WROTE AP — and read statuses to do it; one bad cached status row fataled item.php on 2026-09-30).
// Owner 2026-09-30: front end, scoped to the encounter, nothing for creatures no page holds. So: whenever the page's held encounter
// changes (load, a roster / caster update, any truth lane's render, a status removal) and that encounter is OUT of combat, the rule
// (janus-engine/economy/out-of-combat.ts) plans the rest over the rows THIS viewer may save, and the page posts it:
//   · aps / reaction → the L3 resource lane, kind `ooc_rest` (silent, no log row), on the actor's shared outbox (ordered with the
//     actor's walk / ability / adjust commits by the per-actor revision);
//   · the Summoner Ether pin and the Psion Psi pin (owner 2026-10-16) → the charge lane (`adjust`, log off).
// Who decides a row (the endpoint's ft_actor_truth_authorize twin: the row's owner or the encounter's campaign GM): the owner's page at
// once; the GM's page at once for its own rows and after a grace period for a player's row (so a present player's page rests it first
// and the two pages do not race the same revision). The FT3D scene iframe loads this bundle too: only the top window decides. Only the
// page's ACTIVE encounter is rested, and never from a caster-scoped sheet page (item.php): a sheet render does not write.
import { planOutOfCombatEconomy, type OocEconomyReaders, type OocRestOp } from '../janus-engine/economy/out-of-combat.js';
import { encounterCreatureField, encounterField } from '../shared/array-types.js';
import { traceEncounterTruth } from './encounter-truth-trace.js';
import { definitionOf, readers as chargeReaders, tryCommitCharge } from './charge-truth.js';
import { actorEncounterFor, actorRowFor, pendingFor } from './movement-truth.js';
import { fetchAndCacheObject } from './object-cache.js';
import { tryCommitResolvedResource } from './resource-truth.js';
import { spawnSavePending } from '../shared/spawn-save-pending';
import { entityBasePending } from '../shared/compile-fetched-entity';

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => (Array.isArray(value) ? value[0] : value);
const upper = (value: unknown): string => String(scalar(value) ?? '').trim().toUpperCase();
const emit = (name: string, detail: unknown): void => { window.dispatchEvent(new CustomEvent(name, { detail })); };

const readers: OocEconomyReaders = {
  layerValue: (row, key) => row ? ftLayerValue(row, key) : null,
  entries: chargeReaders.entries,
  definition: definitionOf,
};
/** Debounce after the last change before a plan runs (a burst of frames settles first). */
const SETTLE_MS = 400;
/** How long the GM page leaves a PLAYER's row to that player's own page before resting it itself. */
const GM_GRACE_MS = 3000;
/** A refused / undecidable rest is not retried for the same state within this window. */
const COOLDOWN_MS = 60000;

/** Only the top window decides (the scene iframe loads the same bundle; the encounter page is always the top window). */
function ownsWatcher(): boolean { return window.top === window; }

/** The campaign GM of the held encounter's campaign, as this page holds the campaign. */
function campaignGmOf(encounter: Row): string {
  const campaignId = upper(asRow(encounter.encounters)?.encounter_master_parent_id ?? encounter.encounter_master_parent_id);
  const ft = window.FateTide as unknown as { campaignCache?: Map<string, unknown>; currentCampaignArr?: unknown } | undefined;
  for (const value of [asRow(ft?.campaignCache?.get(campaignId)), asRow(ft?.currentCampaignArr)]) {
    const id = upper(asRow(value?.campaigns)?.campaign_id ?? value?.campaign_id);
    if (value && id === campaignId) return upper(asRow(value.campaigns)?.campaign_gm ?? value.campaign_gm);
  }
  return '';
}
/** 'own' = this viewer owns the row (decide now); 'gm' = the viewer GMs it but a player owns it (decide after the grace); null = neither. */
function authorityFor(encounter: Row, row: Row): 'own' | 'gm' | null {
  const viewer = upper(window.FateTide?.user?.id);
  if (!viewer) return null;
  const owner = upper(asRow(row.encounter_creatures)?.encounter_creature_created_by_id);
  if (owner && owner === viewer) return 'own';
  const gm = campaignGmOf(encounter);
  const isGm = gm ? gm === viewer : window.FateTide?.user?.isGM === true;
  if (!isGm) return null;
  return !owner || owner === gm || owner === viewer ? 'own' : 'gm';
}

const timers = new Map<string, number>();
const scheduledAt = new Map<string, number>();
const running = new Map<string, number>();
let initialWakeAt: number | null = Date.now() + 3000;
const firstSeen = new Map<string, number>();
const cooldown = new Map<string, number>();
/** Definition id → when this page last asked the server for it (a miss is retried after FETCH_RETRY_MS, never remembered forever). */
const fetchedAt = new Map<string, number>();
const FETCH_RETRY_MS = 30000;
const etherInFlight = new Map<string, number>();
// Keep the attempted state through both the optimistic render and older roster echoes.
// A real later resource change (higher saved revision), or a combat boundary, may rest again.
const rested = new Map<string, { rev: number; inFlight: boolean }>();
// Explicit combat assertions survive older compiled buckets, which omit in_combat when zero.
// An explicit exit or a completed encounter read replaces that assertion.
const combatState = new Map<string, boolean>();
// A combat refusal invalidates the encounter, not just this actor's resource projection.
// Hold every automatic pin until a read confirms OOC or a saved exit arrives.
const combatRefused = new Set<string>();
const recovering = new Set<string>();
const combatEpoch = new Map<string, number>();
// A pin's combat-start CAS can arrive before the encounter frame. Wait for that
// boundary instead of re-pinning from the page's older out-of-combat snapshot.
const pinCombatStarted = new Set<string>();
const observed = new Set<string>();
const restActorKey = (encounterId: string, actorId: string): string => encounterId + ':' + actorId;
/** Use the controls' bucket-first reader; omitted zero is OOC unless this encounter asserted combat. */
function knownCombatState(encounterId: string, encounter: Row): boolean {
  const flag = encounterField<unknown>(encounter, 'in_combat');
  if (upper(encounterField(encounter, 'active_combat_id')) || flag === 1 || flag === '1' || flag === true) return true;
  if (flag === 0 || flag === '0' || flag === false) return false;
  return combatState.get(encounterId) ?? false;
}
function clearRested(encounterId: string): void {
  combatEpoch.set(encounterId, (combatEpoch.get(encounterId) ?? 0) + 1);
  for (const key of rested.keys()) if (key.startsWith(encounterId + ':')) rested.delete(key);
}
function observeCombatBoundary(encounterId: string, encounter: Row): void {
  const combat = knownCombatState(encounterId, encounter);
  if (combat) pinCombatStarted.delete(encounterId);
  if (combatState.has(encounterId) && combatState.get(encounterId) !== combat) {
    clearRested(encounterId);
  }
  combatState.set(encounterId, combat);
}
function schedule(encounterId: string, delay = SETTLE_MS): void {
  const id = upper(encounterId);
  if (!id || !ownsWatcher()) return;
  // Observe at event time, so combat entry/exit within the debounce window is not lost.
  const encounter = actorEncounterFor(id) as unknown as Row | null;
  if (encounter && isEncounterPage(id)) observeCombatBoundary(id, encounter);
  const prior = timers.get(id);
  if (prior !== undefined) window.clearTimeout(prior);
  scheduledAt.set(id, Date.now() + delay);
  timers.set(id, window.setTimeout(() => { timers.delete(id); scheduledAt.delete(id); void run(id).catch(error => console.warn('[ooc-rest] plan failed', error)); }, delay));
}
const opKey = (op: OocRestOp): string => op.actorId + ':' + JSON.stringify(op.values ?? null) + ':' + JSON.stringify(op.ether ?? null) + ':' + JSON.stringify(op.psi ?? null);
const valuesKey = (actorId: string, values: { before: { aps: number; reaction: number }; after: { aps: number; reaction: number } }): string =>
  'v:' + actorId + ':' + values.before.aps + '/' + values.before.reaction + '>' + values.after.aps + '/' + values.after.reaction;
const etherKey = (actorId: string, ether: { itemId: string; before: number; after: number }): string => 'e:' + actorId + ':' + ether.itemId + ':' + ether.before + '>' + ether.after;

/** The ENCOUNTER page only: the page's active encounter, never a caster-scoped sheet (item.php marks itself
 * data-ft-page-encounter-scope="caster" — a page render there never writes, R-124). */
function isEncounterPage(encounterId: string): boolean {
  if (typeof document !== 'undefined' && document.querySelector('[data-ft-page-encounter-scope="caster"]')) return false;
  const active = typeof window.ftGetActiveEncounterId === 'function' ? upper(window.ftGetActiveEncounterId()) : '';
  return active !== '' && active === encounterId;
}

/** The plan over what the page holds NOW (rebuilt after any await — Codex review 2026-10-01 P1: never post from rows read before a wait). */
function planNow(encounterId: string): { plan: ReturnType<typeof planOutOfCombatEconomy>; authority: Map<string, 'own' | 'gm'> } | null {
  if (!ownsWatcher() || !isEncounterPage(encounterId)) return null;
  const encounter = actorEncounterFor(encounterId) as unknown as Row | null;
  if (!encounter) return null;
  observeCombatBoundary(encounterId, encounter);
  if (combatRefused.has(encounterId) || combatState.get(encounterId) || pinCombatStarted.has(encounterId)) return null;
  const roster = (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) as Row[];
  const authority = new Map<string, 'own' | 'gm'>();
  const mine: Row[] = [];
  for (const row of roster) {
    const actorId = upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'));
    if (entityBasePending(row) || spawnSavePending(encounterId, actorId, row)) continue;
    const level = authorityFor(encounter, row);
    if (!level) continue;
    authority.set(actorId, level);
    mine.push(row);
  }
  if (!mine.length) return null;
  const plan = planOutOfCombatEconomy(encounter, mine, readers, roster);
  return plan.state === 'out-of-combat' ? { plan, authority } : null;
}

async function run(encounterId: string): Promise<void> {
  running.set(encounterId, (running.get(encounterId) ?? 0) + 1);
  try { await runPlan(encounterId); }
  finally {
    const remaining = (running.get(encounterId) ?? 1) - 1;
    if (remaining) running.set(encounterId, remaining); else running.delete(encounterId);
  }
}

async function runPlan(encounterId: string): Promise<void> {
  let current = planNow(encounterId);
  if (!current) return;
  // A definition this page does not hold is fetched (bounded), then the WHOLE plan is rebuilt from the rows held after the wait;
  // whatever stays unheld is reported, never guessed, and retried later (a failed fetch is not remembered forever — review P2).
  const t0 = Date.now();
  // Fair queue (review 2 P2): never-asked ids first, then the least recently asked — a stuck head never starves the rest.
  const want = current.plan.missing.filter(id => (fetchedAt.get(id) ?? 0) + FETCH_RETRY_MS <= t0)
    .sort((x, y) => (fetchedAt.get(x) ?? 0) - (fetchedAt.get(y) ?? 0)).slice(0, 32);
  if (want.length) {
    want.forEach(id => fetchedAt.set(id, t0));
    const all = Promise.all(want.map(id => fetchAndCacheObject(id).catch(() => null)));
    const settled = await Promise.race([all.then(() => true), new Promise<boolean>(resolve => setTimeout(() => resolve(false), 6000))]);
    if (!settled) void all.then(() => schedule(encounterId)); // a late answer replans when it lands
    current = planNow(encounterId);
    if (!current) return;
  }
  const { plan, authority } = current;
  if (plan.skipped.length) emit('ft:ooc-rest-unresolved', { encounterId, skipped: plan.skipped, missing: plan.missing });
  const now = Date.now();
  let again = plan.missing.length ? FETCH_RETRY_MS : Infinity;
  const wake = (until: number): void => { again = Math.min(again, Math.max(50, until - now + 50)); };
  const live = new Set<string>();
  for (const op of plan.ops) {
    const key = opKey(op);
    live.add(key);
    if (authority.get(op.actorId) === 'gm') {
      const seen = firstSeen.get(key) ?? now;
      firstSeen.set(key, seen);
      if (now - seen < GM_GRACE_MS) { wake(seen + GM_GRACE_MS); continue; }
    }
    // The actor's own outbox work settles first: the rest is planned from the row it leaves, never from an optimistic middle.
    if (pendingFor(encounterId, op.actorId) > 0) { wake(now + 800); continue; }
    // A suppressed op (cooling down / its Ether pin in flight) wakes the plan when the suppression ends (review P2).
    if (op.values) {
      const key = valuesKey(op.actorId, op.values);
      const actorKey = restActorKey(encounterId, op.actorId);
      const row = actorRowFor(encounterId, op.actorId);
      const rev = Math.trunc(Number(scalar(row?.encounter_creatures?.movement_rev) ?? 0));
      const prior = rested.get(actorKey);
      const duplicate = prior && (prior.inFlight || rev <= prior.rev);
      const until = cooldown.get(key) ?? 0;
      if (until > now) wake(until);
      else if (!duplicate) {
        const attempt = { rev, inFlight: true };
        rested.set(actorKey, attempt); // before enqueue: its synchronous optimistic render can trigger another plan
        const outcome = tryCommitResolvedResource({ kind: 'ooc_rest', encounterId, actorId: op.actorId, before: op.values.before, after: op.values.after });
        if (outcome === 'handled') emit('ft:ooc-rest-posted', { encounterId, actorId: op.actorId, values: op.values });
        else if (outcome === 'unchanged') attempt.inFlight = false;
        else { if (rested.get(actorKey) === attempt) rested.delete(actorKey); cooldown.set(key, now + COOLDOWN_MS); wake(now + COOLDOWN_MS); }
      }
    }
    // The full-out-of-combat pools (Ether; Psi since 2026-10-16) — the same pin, each pool on its own in-flight / cooldown key.
    for (const [field, pin] of [['ether', op.ether], ['psi', op.psi]] as const) {
      if (!pin) continue;
      const pool = op.actorId + ':' + pin.itemId;
      const until = Math.max(etherInFlight.get(pool) ?? 0, cooldown.get(etherKey(op.actorId, pin)) ?? 0);
      if (until > now) wake(until);
      else {
        const epoch = combatEpoch.get(encounterId) ?? 0;
        const outcome = tryCommitCharge({ kind: 'adjust', casterId: op.actorId, encounterId, itemId: pin.itemId, delta: pin.after - pin.before, log: false,
          onCombatStarted: () => {
            // A delayed answer from a prior fight cannot suppress a later OOC pin.
            if ((combatEpoch.get(encounterId) ?? 0) === epoch) pinCombatStarted.add(encounterId);
          } });
        if (outcome === 'handled') { etherInFlight.set(pool, now + 25000); wake(now + 25000); emit('ft:ooc-rest-posted', { encounterId, actorId: op.actorId, [field]: pin }); }
        else { cooldown.set(etherKey(op.actorId, pin), now + COOLDOWN_MS); wake(now + COOLDOWN_MS); }
      }
    }
  }
  for (const key of firstSeen.keys()) if (!live.has(key)) firstSeen.delete(key);
  if (firstSeen.size > 512) firstSeen.clear();
  if (cooldown.size > 512) for (const [key, until] of cooldown) if (until <= now) cooldown.delete(key);
  if (fetchedAt.size > 512) fetchedAt.clear();
  if (again !== Infinity) schedule(encounterId, again);
}

// Combat refusals require encounter recovery; other refusals cool down for that state.
window.addEventListener('ft:resource-truth-refused', event => {
  const detail = (event as CustomEvent).detail;
  const command = asRow(detail?.command);
  if (command?.kind !== 'ooc_rest') return;
  const encounterId = upper(command.encounter_id);
  if (detail?.info?.status === 409 && detail.info.error === 'In combat') {
    combatRefused.add(encounterId);
    const key = restActorKey(encounterId, upper(command.actor_id));
    const prior = rested.get(key);
    if (prior) rested.set(key, { ...prior, inFlight: false });
    // Keep the attempted actor's dedupe. A failed or inconclusive read never starts a cooldown retry.
    if (!recovering.has(encounterId)) {
      recovering.add(encounterId);
      const finish = traceEncounterTruth('ooc-rest-combat-recovery', encounterId, { operationId: String(command.operation_id ?? '') });
      void Promise.resolve().then(() => window.ftResyncEncounter?.(encounterId))
        .catch(error => console.warn('[ooc-rest] combat recovery failed', error)).finally(() => {
          recovering.delete(encounterId);
          finish?.();
        });
    }
    return;
  }
  rested.delete(restActorKey(upper(command.encounter_id), upper(command.actor_id)));
  const before = asRow(command.before), after = asRow(command.after);
  const n = (v: unknown): number => Math.trunc(Number(v ?? 0)) || 0;
  cooldown.set(valuesKey(upper(command.actor_id), { before: { aps: n(before?.aps), reaction: n(before?.reaction) }, after: { aps: n(after?.aps), reaction: n(after?.reaction) } }), Date.now() + COOLDOWN_MS);
  schedule(upper(command.encounter_id), COOLDOWN_MS + 50); // review 2 P2: the cooldown's end wakes the plan
});
window.addEventListener('ft:ooc-rest-settled', event => {
  const detail = (event as CustomEvent).detail;
  const command = asRow(detail?.command);
  if (command?.kind !== 'ooc_rest' || !Number.isInteger(detail?.rev)) return;
  const key = restActorKey(upper(command.encounter_id), upper(command.actor_id));
  const prior = rested.get(key);
  // An answer from a prior combat boundary must not settle a newer attempt.
  if (prior?.inFlight && prior.rev === command.expected_rev) {
    if (detail.done === false) rested.delete(key); // mismatching saved values need a fresh plan
    else rested.set(key, { ...prior, rev: detail.rev, inFlight: false });
  }
});
window.addEventListener('ft:ws-state-recovery', event => {
  const detail = (event as CustomEvent).detail;
  if (detail?.kind !== 'encounter' || detail.status !== 'recovered') return;
  const id = upper(detail.id);
  const encounter = actorEncounterFor(id) as unknown as Row | null;
  if (!encounter) return;
  // Fresh read truth replaces the combat assertion, including omitted zero, while preserving actor dedupe.
  combatState.delete(id);
  observeCombatBoundary(id, encounter);
  if (combatState.get(id)) return;
  combatRefused.delete(id);
  schedule(id);
});
for (const name of ['ft:charge-truth-committed', 'ft:charge-truth-reconciled', 'ft:charge-truth-refused']) {
  window.addEventListener(name, event => {
    const body = asRow((event as CustomEvent).detail?.body);
    if (!body) return;
    etherInFlight.delete(upper(body.caster_id) + ':' + upper(body.item_id));
    // A refused pin cools down for that exact state (an Ether pin is never a log-bearing click to retry blindly).
    if (name === 'ft:charge-truth-refused' && body.log === false && Number.isInteger(body.before) && Number.isInteger(body.after)) {
      cooldown.set(etherKey(upper(body.caster_id), { itemId: upper(body.item_id), before: Number(body.before), after: Number(body.after) }), Date.now() + COOLDOWN_MS);
      schedule(upper(body.encounter_id), COOLDOWN_MS + 50);
    }
  });
}
// Every change to the held encounter replans it: load / roster / turn state, and any caster row a truth lane or a frame re-rendered.
for (const name of ['ft:encounter-state-received', 'ft:encounter-turn-state', 'ft:caster-state-applied', 'ft:charge-truth-applied', 'ft:spawn-save-settled', 'ft:entity-base-compiled', 'ft:inventory-base-compiled']) {
  window.addEventListener(name, (event: Event) => {
    try {
      const detail = (event as CustomEvent).detail;
      const id = upper(detail?.encounterId ?? (name.endsWith('-base-compiled') ? window.ftGetActiveEncounterId?.() : ''));
      if (!id) return;
      if (name === 'ft:encounter-turn-state' && typeof detail?.inCombat === 'boolean' && detail.combatStateAsserted === true
        && ['combat-truth', 'encounter-stats-update'].includes(detail?.source)) {
        if (combatState.has(id) && combatState.get(id) !== detail.inCombat) clearRested(id);
        combatState.set(id, detail.inCombat);
        if (detail.inCombat === false && combatRefused.delete(id)) clearRested(id);
      }
      traceEncounterTruth(name, id, { source: detail?.source, subType: detail?.subType })?.();
      observed.add(id);
      schedule(id);
    } catch { /* never breaks the event */ }
  });
}
// The page's active encounter once the bundle has settled (a load that dispatched its encounter event before this module listened).
window.setTimeout(() => { initialWakeAt = null; try { const id = upper(window.ftGetActiveEncounterId?.()); if (id && !observed.has(id)) schedule(id); } catch { /* optional */ } }, 3000);

/** Smokes / diagnostics: run the plan for an encounter now (no debounce). */
function ftOocRestNow(encounterId: string): Promise<void> { return run(upper(encounterId)); }
/** Read-only teardown diagnostics: include definition hydration and the initial delayed wake. */
function ftOocRestWorkState(encounterId: string): { dueAt: number | null; running: number } {
  const id = upper(encounterId);
  const dueAt = Math.min(scheduledAt.get(id) ?? Infinity, !observed.has(id) ? initialWakeAt ?? Infinity : Infinity);
  return { dueAt: Number.isFinite(dueAt) ? dueAt : null, running: running.get(id) ?? 0 };
}
Object.assign(window, { ftOocRestNow, ftOocRestWorkState });
